'use client';

import React from 'react';
import Link from 'next/link';
import { Info } from 'lucide-react';
import VariantDetails from '../ProductModelSwitcher/VariantDetails';

export default function ComparisonCard({ car, showStandardEquipment, isSelected }) {
  if (!car) return null;

  const image = car.main_image || car.detail_image_1 || car.slider_image || '';

  // Performance Data
  const acceleration = car.technical_info?.acceleration_0_100_s ? `${car.technical_info.acceleration_0_100_s} s` : "-";
  const power = car.technical_info?.gasoline_engine_power_hp
    ? `${car.technical_info.gasoline_engine_power_hp} hp`
    : (car.technical_info?.electric_motor_power_kw ? `${car.technical_info.electric_motor_power_kw} kW` : "-");
  const topSpeed = car.technical_info?.top_speed_kmh ? `${car.technical_info.top_speed_kmh} km/h` : "-";

  // Standard Equipment (Fallback generic or mapped)
  const sunroof = car.interior?.sunroof ? "سانروف" : (car.interior?.sunroof === false ? "بدون سانروف" : "سانروف: -");
  const cruise = car.adas?.cruise_control ? "کروز کنترل" : (car.adas?.cruise_control === false ? "بدون کروز کنترل" : "کروز کنترل: -");
  const airbags = car.safety?.airbags ? `کیسه هوا: ${car.safety.airbags}` : "کیسه هوا: -";

  return (
    <div className={`flex flex-col border rounded-card-mob md:rounded-card-desk p-6 pt-20 md:p-8 md:pt-28 h-full relative group hover:shadow-lg transition-all duration-300 min-h-[700px] text-right mt-16 md:mt-24 ${
      isSelected ? 'bg-gray-200 border-gray-300 shadow-md ring-1 ring-gray-300' : 'bg-white border-gray-200'
    }`} dir="rtl">

      {/* Car Image (Perfectly Centered 50% above card border) */}
      <div className="absolute top-0 left-1/2 w-[90%] md:w-[105%] -translate-x-1/2 -translate-y-1/2 flex justify-center items-center z-10 pointer-events-none">
        {image ? (
          <img src={image} alt={car.english_name} className="max-w-full h-auto max-h-[220px] object-contain drop-shadow-xl" />
        ) : (
          <div className="w-48 h-full bg-gray-50 rounded-xl flex items-center justify-center text-gray-400 border border-gray-200 shadow-sm pointer-events-auto">
            بدون تصویر
          </div>
        )}
      </div>

      {/* Title & Price Placeholder */}
      <div className="mb-4">
        <h3 className="text-2xl font-bold text-gray-900 latinNumbers mb-2 text-left" dir="ltr">{car.english_name}</h3>
      </div>

      {/* Tags row */}
      <div className="flex flex-wrap gap-2 mb-6 justify-end relative z-20">
        <span className="bg-black text-white text-xs font-bold px-3 py-1 rounded-small-button-mob md:rounded-small-button-desk latinNumbers">
          {car.production_year || '2025'}
        </span>
        <span className="bg-gray-100 text-gray-800 text-xs font-semibold px-3 py-1 rounded-small-button-mob md:rounded-small-button-desk">
          {car.fuel_type || '-'}
        </span>
        <span className="bg-gray-100 text-gray-800 text-xs font-semibold px-3 py-1 rounded-small-button-mob md:rounded-small-button-desk">
          {car.technical_info?.drivetype || '-'}
        </span>
        <span className="bg-gray-100 text-gray-800 text-xs font-semibold px-3 py-1 rounded-small-button-mob md:rounded-small-button-desk">
          {car.technical_info?.gearbox || '-'}
        </span>
      </div>

      {/* Specs Section (Swaps based on toggle) */}
      <div className="flex-grow">
        {!showStandardEquipment ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between" dir="ltr">
              <div className="text-2xl font-bold text-gray-900 latinNumbers text-left">{acceleration}</div>
              <div className="text-xs text-gray-500" dir="rtl">شتاب 0 - 100</div>
            </div>
            <div className="flex items-center justify-between" dir="ltr">
              <div className="text-2xl font-bold text-gray-900 latinNumbers text-left">{power}</div>
              <div className="text-xs text-gray-500" dir="rtl">قدرت موتور</div>
            </div>
            <div className="flex items-center justify-between" dir="ltr">
              <div className="text-2xl font-bold text-gray-900 latinNumbers text-left">{topSpeed}</div>
              <div className="text-xs text-gray-500" dir="rtl">حداکثر سرعت</div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between" dir="ltr">
              <div className="text-lg font-bold text-gray-900 text-left">{sunroof}</div>
              <div className="text-xs text-gray-500" dir="rtl">سقف</div>
            </div>
            <div className="flex items-center justify-between" dir="ltr">
              <div className="text-lg font-bold text-gray-900 text-left">{cruise}</div>
              <div className="text-xs text-gray-500" dir="rtl">امکانات رفاهی</div>
            </div>
            <div className="flex items-center justify-between" dir="ltr">
              <div className="text-lg font-bold text-gray-900 text-left">{airbags}</div>
              <div className="text-xs text-gray-500" dir="rtl">ایمنی</div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Disclaimer */}
      <div className="mt-8 mb-4">
        <p className="text-[10px] text-gray-400 leading-tight">
          * اطلاعات فنی و آپشن‌ها بر اساس کاتالوگ شرکت سازنده می‌باشد.
        </p>
      </div>

      {/* Actions */}
      <div className="mt-auto flex flex-col gap-3">
        <div className="flex gap-3 mt-4">
          <VariantDetails
            car={car}
            isLinkStyle={false}
            triggerText="مشخصات"
            buttonClassName="flex-1 bg-gray-300 hover:bg-gray-100 text-[#2b2b2b] text-center py-3 w-full rounded-button-mob md:rounded-button-desk font-bold transition-colors text-sm"
          />
        </div>
      </div>

    </div>
  );
}
